import { TableColumnForTableExport } from '@doss/common-all'
import { Collapsible } from '~/@/components/ui/collapsible'
import { BooleanOperator, TableFilterInput } from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { ActiveUser } from '~/components/settings/types'
import { formatFiltersForDisplay } from './exportFilterSummary'

interface ExportFilterPreviewProps {
  filters: TableFilterInput[]
  columns: TableColumnForTableExport[]
  booleanOperator?: BooleanOperator
  activeUsers?: ActiveUser[]
}

/**
 * Displays a collapsible preview of filters that will be applied during export.
 * Shows the column name, operator, and value for each filter.
 */
export function ExportFilterPreview({
  filters,
  columns,
  booleanOperator = BooleanOperator.AND,
  activeUsers,
}: ExportFilterPreviewProps) {
  const { t } = useTypedTranslation()

  if (filters.length === 0) {
    return (
      <div className="rounded bg-gray-2 p-3">
        <span className="text-sm text-gray-9">{t('table.export.no_filters')}</span>
      </div>
    )
  }

  const filterDisplayInfos = formatFiltersForDisplay(filters, columns, t, activeUsers)
  const connectorText =
    booleanOperator === BooleanOperator.AND
      ? t('table.export.filter_summary_and')
      : t('table.export.filter_summary_or')

  return (
    <Collapsible
      defaultOpen
      title={
        <span className="text-xs text-gray-9">
          {t('table.export.filters_being_exported')} ({filters.length})
        </span>
      }
    >
      <div className="mt-2 flex flex-col gap-2 rounded bg-gray-2 p-3">
        {filterDisplayInfos.map((info, index) => (
          <div key={index} className="flex flex-wrap items-center gap-1">
            {index > 0 && <span className="me-1 text-sm italic text-gray-9">{connectorText}</span>}
            <span className="text-sm font-medium">{info.columnName}</span>
            <span className="text-sm text-gray-9">{info.operatorDisplay}</span>
            {info.valueDisplay && (
              <span className="max-w-[200px] truncate rounded bg-gray-3 px-2 py-0.5 text-sm">
                {info.valueDisplay}
              </span>
            )}
          </div>
        ))}
      </div>
    </Collapsible>
  )
}
